import {
  BillingBusinessLicense,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { CSSProperties, forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanCard } from './PlanCard';
import { arePlansEqual, getUnifiedPlan, UnifiedPlan } from '../util';
import useModal from '__components/Modals/useModal';
import { WarningModal } from './WarningModal';
import { CurrentlyActivePlan } from '../hooks/useSelectedPlanResolution';

type PlanRowProps = {
  plans: BillingLicensePlans['license_templates'];
  title: string;
  period: string;
  unit?: string;
  currentPlan?: BillingBusinessLicense[number];
  selectedPlanIndex: number | null;
  setSelectedPlanIndex?: (index: number, plan: UnifiedPlan | undefined) => void;
  numberInput?: boolean;
  numberInputLabel?: string;
  numberInputValue?: number;
  onNumberInputValueChange?: (value: number) => void;
  selectable?: boolean;
  scrollToGeneralPlanRow?: () => void;
  pendingDowngradePlan?: CurrentlyActivePlan;
  onCancelDowngrade: (id: string) => void;
  downgradeDate?: string;
  freePlanSelectable?: boolean;
};

export const PlanRow = forwardRef<HTMLDivElement, PlanRowProps>(
  (props, ref) => {
    const {
      plans,
      title,
      period,
      unit,
      currentPlan,
      selectedPlanIndex: selectedPlanIndexProp,
      setSelectedPlanIndex,
      numberInput,
      numberInputLabel,
      numberInputValue,
      onNumberInputValueChange,
      selectable = true,
      scrollToGeneralPlanRow,
      pendingDowngradePlan,
      onCancelDowngrade,
      downgradeDate,
      freePlanSelectable = true,
    } = props;

    // TODO: Fix this type once the schemas are correct
    const pendingDowngradeMaxUsers = (
      pendingDowngradePlan?.config?.limits as any
    )?.max_users;

    const [warningModalProps, , warningModal] = useModal({
      handleSubmit: () => {
        scrollToGeneralPlanRow?.();
      },
      handleCancel: () => null,
    });

    const [warningModalDowngradeProps, , warningModalDowngrade] = useModal({
      handleSubmit: () => {
        scrollToGeneralPlanRow?.();
      },
      handleCancel: () => null,
    });

    const { t } = useTranslation();

    const activePlanIndex = useMemo(() => {
      return plans.findIndex((plan) => {
        return arePlansEqual(plan, currentPlan);
      });
    }, [currentPlan, plans]);

    const downgradePlanIndex = useMemo(() => {
      return plans.findIndex((plan) => {
        return arePlansEqual(plan, pendingDowngradePlan);
      });
    }, [pendingDowngradePlan, plans]);

    const hasDowngradePlan = Boolean(pendingDowngradePlan);
    const hasLegacyDowngradePlan = hasDowngradePlan && downgradePlanIndex < 0;

    const hasLegacyPlan = activePlanIndex < 0;

    const selectedPlanIndex =
      selectedPlanIndexProp === null ? activePlanIndex : selectedPlanIndexProp;

    return (
      <div className="flex flex-col" ref={ref}>
        <div>
          <Typography weight="semibold" size="lg">
            {title} - {t('Select Plan')}
          </Typography>
        </div>
        <Spacer size={30} />
        <div
          className="grid grid-cols-[repeat(var(--column-count),minmax(0,1fr))] w-full gap-20"
          style={
            {
              '--column-count': hasLegacyPlan ? plans.length + 1 : plans.length,
            } as CSSProperties
          }
        >
          {hasLegacyPlan ? (
            <PlanCard
              currentPlan={currentPlan}
              isCurrentPlan
              isSelectedPlan={selectedPlanIndex === -1}
              period={period}
              unit={unit}
              isLegacy
              onSelectPlan={() => {
                /*
                  The legacy plan is always selectable no matter what,
                  since it's guaranteed to be your current plan
                */
                return setSelectedPlanIndex?.(
                  -1,
                  getUnifiedPlan(undefined, currentPlan)
                );
              }}
              numberInput={numberInput}
              numberInputLabel={numberInputLabel}
              numberInputValue={numberInputValue}
              onNumberInputValueChange={onNumberInputValueChange}
              pendingDowngradeMaxUsers={pendingDowngradeMaxUsers}
            />
          ) : null}
          {hasLegacyDowngradePlan ? (
            <PlanCard
              currentPlan={currentPlan}
              isDowngradePlan
              period={period}
              unit={unit}
              onCancelDowngrade={() => {
                if (pendingDowngradePlan) {
                  return onCancelDowngrade?.((pendingDowngradePlan as any)?.id);
                }
              }}
              numberInput={numberInput}
              numberInputLabel={numberInputLabel}
              numberInputValue={numberInputValue}
              onNumberInputValueChange={onNumberInputValueChange}
              downgradeDate={downgradeDate}
              pendingDowngradeMaxUsers={pendingDowngradeMaxUsers}
            />
          ) : null}
          {plans
            .map((plan, elementIndex) => {
              const isCurrentPlan = elementIndex === activePlanIndex;
              const isDowngradePlan = elementIndex === downgradePlanIndex;
              const isSelectedPlan = elementIndex === selectedPlanIndex;
              const isPaid = plan.price.amount > 0;

              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  period={period}
                  unit={unit}
                  isCurrentPlan={isCurrentPlan}
                  isDowngradePlan={isDowngradePlan}
                  isSelectedPlan={isSelectedPlan}
                  onSelectPlan={() => {
                    /*
                    If the plan is not selectable, and it's not free, 
                    it can't be chosen without a paid core plan, so we show
                    a warning
                  */
                    if (!selectable && !isCurrentPlan && isPaid) {
                      return warningModal.show();
                    }

                    if (!isPaid && !freePlanSelectable) {
                      return warningModalDowngrade.show();
                    }

                    return setSelectedPlanIndex?.(
                      elementIndex,
                      getUnifiedPlan(plan, undefined)
                    );
                  }}
                  onCancelDowngrade={() => {
                    if (pendingDowngradePlan) {
                      return onCancelDowngrade?.(
                        (pendingDowngradePlan as any)?.id
                      );
                    }
                  }}
                  numberInput={numberInput}
                  numberInputLabel={numberInputLabel}
                  numberInputValue={numberInputValue}
                  onNumberInputValueChange={onNumberInputValueChange}
                  downgradeDate={downgradeDate}
                  pendingDowngradeMaxUsers={pendingDowngradeMaxUsers}
                />
              );
            })
            .filter(Boolean)}
        </div>
        <WarningModal modalProps={warningModalProps} planTitle={title} />
        <WarningModal
          corePlanSelection
          modalProps={warningModalDowngradeProps}
          planTitle={title}
        />
      </div>
    );
  }
);
